import {
  getSearchHistoryDashboard,
  postSearchHistoryDashboard,
  deleteSearchHistoryDashboard,
  getSearchSuggestions,
  getTrendingTags,
  getSearch,
  getAnonymousSearchHistory,
  postAnonymousSearchHistory,
  deleteAnonymousSearchHistory,
} from "../apiFunctions";
import { appConfig } from "@/config/appConfig";
import { getGuestUUID, ensureGuestUUID } from "../tokenCheck";
import { eventBus } from "../eventBus";

export default {
  data() {
    return {
      isSearchLoading: false,
      searchSuggestionRequestId: 0,
      recentSearches: [],
      trendingTags: [],
      isRecentSearchLoading: false,
      isTrendingTagLoading: false,
      appConfig,
    };
  },

  created() {
    if (!this.hasAccess()) {
      ensureGuestUUID();
    }

    eventBus.$on("login-success", this.handleLoginSuccessRecentSearches);
    eventBus.$on("refresh-recent-searches", this.handleRefreshRecentSearches);
  },
  beforeDestroy() {
    eventBus.$off("login-success", this.handleLoginSuccessRecentSearches);
    eventBus.$off("refresh-recent-searches", this.handleRefreshRecentSearches);
    if (this._searchDebounceTimer) clearTimeout(this._searchDebounceTimer);
  },
  methods: {
    normalizeRecentSearchesResponse(response) {
      console.info("Normalizing recent searches response:", response);
      const candidates = [
        response?.data?.data?.data,
        response?.data?.data?.items,
        response?.data?.data?.results,
        response?.data?.data?.recentSearches,
        response?.data?.data,
        response?.data,
      ];

      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          console.info("Found valid recent searches array:", candidate);
          return candidate;
        }
      }

      console.warn("No valid recent searches array found in response");
      return [];
    },
    hasAccess() {
      // Use Vuex state to determine access, matching portal logic
      return this.$store.getters["userVUEX/getCandAccess"];
    },
    async trackSearchInteraction(query, type = "job") {
      if (!query) return;
      try {
        let numericType = 1;
        if (type === "assessment" || type === "assessments" || type === 2)
          numericType = 2;
        else if (type === "course" || type === "courses" || type === 3)
          numericType = 3;

        const payload = { searchQuery: query, type: numericType };

        // Handle Guest vs Logged-in search history
        if (this.hasAccess()) {
          console.info("Posting to user search-history with payload:", payload);
          // User is logged in, save to primary search history
          await postSearchHistoryDashboard(payload);
        } else {
          // User is Guest, save to anonymous history using browser UUID
          const guestUUID = ensureGuestUUID();
          payload.uuid = guestUUID;
          console.info("Posting to anonymous search-history with payload:", payload);
          await postAnonymousSearchHistory(payload);
        }
        eventBus.$emit("refresh-recent-searches", type);
      } catch (error) {
        console.error("Tracking Error:", error);
      }
    },
    handleRefreshRecentSearches(type) {
      // Opt out if this component instance doesn't display recent searches
      if (this.$options.skipRecentSearchRefresh) return;
      const currentTab = this.selectedTab || "job";
      const eventType = type || "job";
      // Refresh if the updated history type matches current tab
      if (eventType === currentTab) {
        this.getRecentSearches(eventType);
      }
    },
    handleLoginSuccessRecentSearches() {
      const currentTab = this.selectedTab || "job";
      this.getRecentSearches(currentTab);
    },
    async getRecentSearches(type = "job", count = 10) {
      if (this.isRecentSearchLoading) return; // Prevent duplicate calls if one is already in progress

      this.isRecentSearchLoading = true;
      try {
        let numericType = 1;
        if (type === "assessment" || type === "assessments" || type === 2)
          numericType = 2;
        else if (type === "course" || type === "courses" || type === 3)
          numericType = 3;

        const payload = { type: numericType, count };
        let res;

        // Fetch history from correct source based on login state
        if (this.hasAccess()) {
          console.info("Fetching registered user recent searches with payload:", payload);
          // Fetch registered user history
          res = await getSearchHistoryDashboard(payload);
        } else {
          // Fetch Guest history using UUID from cookies
          const uuid = getGuestUUID();
          console.info("Fetching anonymous recent searches with uuid:", uuid, "payload:", payload);
          if (!uuid) {
            console.warn("No guest_uuid found in cookies, skipping recent searches fetch");
            this.recentSearches = [];
            return;
          }
          payload.uuid = uuid;
          res = await getAnonymousSearchHistory(payload);
        }

        if (res && res.status == 200) {
          console.info("Recent searches API call successful");
          // Safety fallback: handles different backend response structures
          this.recentSearches = this.normalizeRecentSearchesResponse(res);
        } else {
          console.warn("Unexpected recent searches response status:", res?.status, res);
        }
      } catch (error) {
        console.error("Recent Search Error:", error);
      } finally {
        this.isRecentSearchLoading = false;
      }
    },
    async clearRecentSearches(type = null) {
      const searches = this.recentSearches;
      if (!searches || searches.length === 0) return;
      try {
        const resolvedType =
          type && typeof type === "string" ? type : this.selectedTab || "job";
        let numericType = 1;
        if (
          resolvedType === "assessment" ||
          resolvedType === "assessments" ||
          resolvedType === 2
        )
          numericType = 2;
        else if (
          resolvedType === "course" ||
          resolvedType === "courses" ||
          resolvedType === 3
        )
          numericType = 3;

        const searchIDs = searches
          .map((item) => item.searchID)
          .filter((id) => id);

        if (this.hasAccess()) {
          const payload = { searchIDs, type: numericType, deleteAll: true };
          await deleteSearchHistoryDashboard(payload);
        } else {
          const uuid = getGuestUUID();
          if (uuid) {
            await deleteAnonymousSearchHistory({ uuid, searchIDs });
          }
        }
        this.recentSearches = [];
        eventBus.$emit("refresh-recent-searches", resolvedType);
      } catch (error) {
        console.error("Clear Error:", error);
        this.recentSearches = [];
      }
    },
    async getSearchSuggestionsData(query, type = "job") {
      const normalizedQuery = (query ?? "").toString().trim();
      if (!normalizedQuery) {
        this.searchSuggestion = [];
        this.searchSuggestionRequestId += 1;
        return;
      }

      // requestId prevents "Race Conditions" where slow requests overwrite newer ones
      const requestId = ++this.searchSuggestionRequestId;
      this.isSearchLoading = true;
      try {
        let res;
        let nextSuggestions = [];
        if (type === "courses" || type === "course" || type === 3) {
          // Use searchQuery as the primary parameter for course suggestions
          res = await getSearch(normalizedQuery);
          if (res.status == 200) {
            nextSuggestions =
              res.data?.data?.courseInfo?.data || res.data?.data?.data || [];
          }
        } else {
          let payload = {
            searchQuery: normalizedQuery,
            type:
              type === "assessment" || type === "assessments" || type === 2
                ? 2
                : 1,
          };
          res = await getSearchSuggestions(payload);
          if (res.status == 200) {
            nextSuggestions = res.data?.data?.Detail || [];
          }
        }

        if (requestId === this.searchSuggestionRequestId) {
          this.searchSuggestion = nextSuggestions;
        }
      } catch (error) {
        console.error("Search API Error:", error);
        if (requestId === this.searchSuggestionRequestId) {
          this.searchSuggestion = [];
        }
      } finally {
        if (requestId === this.searchSuggestionRequestId) {
          this.isSearchLoading = false;
        }
      }
    },
    onSearchInput(event) {
      const query = (event?.target?.value ?? event ?? "").toString().trim();
      this.searchQuery = query;
      if (this._searchDebounceTimer) clearTimeout(this._searchDebounceTimer);
      this._searchDebounceTimer = setTimeout(function() {
        this.getSearchSuggestionsData(query, this.selectedTab);
      }.bind(this), 300);
    },
    async getTrendingTagsData(type = "job") {
      this.isTrendingTagLoading = true;
      try {
        let typeParam = type;
        if (type === 1 || type === "job" || type === "jobs") typeParam = "job";
        else if (type === 2 || type === "assessment" || type === "assessments")
          typeParam = "assessment";
        else if (type === 3 || type === "course")
          typeParam = "course";

        const res = await getTrendingTags({ type: typeParam });
        if (res.status == 200) {
          const trendingData = res.data?.data;
          this.trendingTags = Array.isArray(trendingData)
            ? trendingData
            : Array.isArray(trendingData?.data)
              ? trendingData.data
              : Array.isArray(trendingData?.items)
                ? trendingData.items
                : [];
        }
      } catch (error) {
        console.error("Trending Tags Error:", error);
      } finally {
        this.isTrendingTagLoading = false;
      }
    },
    handleEnterSearch(query, type = "job") {
      const isEventObject =
        query &&
        typeof query === "object" &&
        (query.target || query.currentTarget || query.type || query.key);
      const eventStringPattern = /^\[object\s+.+Event\]$/;
      const normalizedRawQuery = isEventObject
        ? query?.target?.value || this.searchQuery
        : query;
      let resolvedQuery = (normalizedRawQuery ?? "").toString().trim();

      if (eventStringPattern.test(resolvedQuery)) {
        resolvedQuery = (this.searchQuery || "").toString().trim();
      }
      const resolvedType = isEventObject ? this.selectedTab || type : type;

      if (!resolvedQuery) return;
      this.trackSearchInteraction(resolvedQuery, resolvedType);

      const encodedQuery = encodeURIComponent(resolvedQuery);

      if (
        resolvedType === "job" ||
        resolvedType === "jobs" ||
        resolvedType === 1
      ) {
        window.open(
          `${appConfig.candidateBase}/job/search?searchQuery=${encodedQuery}&tab=hire&from=candidate`,
          "_self",
        );
      } else if (
        resolvedType === "assessment" ||
        resolvedType === "assessments" ||
        resolvedType === 2
      ) {
        window.open(
          `${appConfig.candidateBase}/assessment/progress?searchQuery=${encodedQuery}&from=candidate`,
          "_self",
        );
      } else if (
        resolvedType === "courses" ||
        resolvedType === "course" ||
        resolvedType === 3
      ) {
        this.$router.push({
          name: "FilteredCoursesView",
          query: {
            sortBy: "publishedAt",
            search: resolvedQuery,
          },
        });
        if (this.closeSearchDropdown) {
          this.closeSearchDropdown();
        }
        this.$emit("close-modal");
      } else {
        window.open(
          `${appConfig.candidateBase}/job/search?searchQuery=${encodedQuery}&tab=hire&from=candidate`,
          "_self",
        );
      }

    },
  },
};
